import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const DD_KEYS = ['dd_metric', 'dd_from', 'dd_to', 'dd_team', 'dd_user', 'dd_status'];

/**
 * Reads the dashboard drill-down from the URL (set by clicking a dashboard card).
 * `params` are sent to the list API as-is; the backend applies the same query the
 * dashboard used (see backend DashboardDrilldown), so the list matches the card.
 */
export const useDashboardDrilldown = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryString = DD_KEYS.map((k) => searchParams.get(k) ?? '').join('|');
  const label = searchParams.get('dd_label') || '';

  const params = useMemo(() => {
    const out = {};
    DD_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- queryString captures every dd_* value
  }, [queryString]);

  const active = Boolean(params.dd_metric);

  const clear = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        [...DD_KEYS, 'dd_label'].forEach((k) => next.delete(k));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return { active, params, label, clear };
};

/** Lower-cases and strips punctuation so "In Process", "in_process" and "In-Process" compare equal. */
export const normalizeFilterKey = (v) => String(v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Shows a dashboard drill-down in the list page's own filter controls.
 * - `value`: what to put in the filter (`undefined` = nothing to show, or options not loaded yet).
 * - `apply(value)` sets the filter; `reset()` undoes it when the drill-down is cleared (banner "Clear").
 * - the returned `detach()`: call when the user edits the filter themselves — it drops the
 *   drill-down from the URL without resetting the filter they just chose.
 */
export const useDrilldownFilterSync = ({ active, value, apply, reset, clear }) => {
  const appliedRef = useRef(false);
  const applyRef = useRef(apply);
  const resetRef = useRef(reset);
  applyRef.current = apply;
  resetRef.current = reset;

  useEffect(() => {
    if (active && value !== undefined && !appliedRef.current) {
      appliedRef.current = true;
      applyRef.current(value);
    } else if (!active && appliedRef.current) {
      appliedRef.current = false;
      resetRef.current();
    }
  }, [active, value]);

  return useCallback(() => {
    appliedRef.current = false;
    if (active) clear();
  }, [active, clear]);
};

export default useDashboardDrilldown;
